import React, { ReactElement } from "react";
// SnGrid_popup.tsx
import ResetPwdPopup from "@widgets/commonpopup/ResetPwdPopup";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { showPopupModal } from "@widgets/CommonModal";
import {
  getSearchData,
  getInitPopUp,
  getInitPopUpData,
} from "@shared/utils/CommonUtils";
import { ColumnConfig } from "./SnGridTypes";
import { RowNode } from "ag-grid-enterprise";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import MultiPopup from "@widgets/commonpopup/MultiPopup";
import { RowData } from "./SnGridTypes";
import { setPopupValue, clearPopupValueMarks } from "./SnGridPopupFlag";
import { getParentFields } from "./SnColumnsCreate";

export async function PopupHelper(
  gridApi: any,
  params: any,
  popupConditions: any,
  columnConfig: ColumnConfig[],
  addRow: (newRow?: RowData, useEvent?: boolean) => RowNode | null
): Promise<{ applyNodes: RowNode[]; isMove: boolean }> {
  if (!gridApi || !params || !params.colDef.columnConfig)
    return { applyNodes: [], isMove: false };
  const rowNode = params.api.getRowNode(params.node.id);
  const popupId = params.colDef.columnConfig.cd_popup;
  const colId = params.colDef.field;
  const colType = params.colDef.columnConfig.tp_column;
  const cdClas = params.colDef.columnConfig.cd_clas;
  const popupApply = params.colDef.columnConfig.popup_apply;
  const applyNodes: RowNode[] = [];
  let isMove: boolean = false;
  const stopGridEditing = () => {
    const api = gridApi?.current ?? gridApi;
    const editingCells = api?.getEditingCells?.() ?? [];
    if (editingCells.length > 0) {
      api.stopEditing(true);
    }
  };

  // 26.08.21 ysw 값이 실제로 바뀌지 않아 콜백이 안 불린 경우 등 이전 세션의 미소비 표시 정리
  clearPopupValueMarks();

  const popupSetting = PopupSettings[popupId];
  if (!popupSetting) return { applyNodes: applyNodes, isMove: isMove };

  const columnFields = new Set(columnConfig.map((col) => col.cd_column));

  // 26.10.26 잠긴 상위 코드 컬럼(예: 저장된 행의 품목)의 필드는 code/name(0,1번)이 아니면 건드리지 않는다
  // (getParentFields 참고. 코드 직접 입력 시 codeValueChanged 와 같은 기준)
  const lockedByNode = new Map<any, Set<string>>();
  const skipRest = (node: any, idx: number, field: string) => {
    if (idx <= 1) return false;
    if (!lockedByNode.has(node)) {
      lockedByNode.set(
        node,
        getParentFields(
          gridApi?.current ?? gridApi,
          node,
          colId,
          popupSetting.columns,
          true
        )
      );
    }
    return lockedByNode.get(node)!.has(field);
  };

  const baseConditions = rowNode.data || {};
  const mergedConditions = popupConditions[colId]
    ? { ...baseConditions, ...popupConditions[colId] }
    : baseConditions;
  const keyValueObject = {
    str_search: rowNode.data[colId] || "",
    ...mergedConditions,
  };

  if (popupId === "POPUP_PASSWORD") {
    const passwordPopup = <ResetPwdPopup />;
    const result = await showPopupModal({
      title: "비밀번호 변경",
      children: passwordPopup,
      width: "300px",
      height: "275px",
    });

    if (result !== undefined) {
      if (result === "close") {
        return { applyNodes: applyNodes, isMove: isMove };
      }

      if (rowNode) {
        stopGridEditing();
        rowNode.setDataValue(colId, result);
      }
    }
    isMove = true;
    return { applyNodes: applyNodes, isMove: isMove };
  }

  if (colType === "SnCodeBox") {
    applyNodes.push(rowNode);

    const popupData = getSearchData(popupId, "");

    const { columns } = popupSetting;

    const rtn: any = await getInitPopUpData(popupId, {});
    const mergedData = {
      ...rtn.formData,
      ...keyValueObject,
      cd_clas: cdClas || "",
    };
    const singlePopup = (
      <SinglePopup
        code={popupId}
        selectedData={mergedData}
        autoSearch={popupData.autoSearch}
      />
    );

    const result = await showPopupModal({
      title: popupData.title,
      children: singlePopup,
      width: popupData.width,
      height: popupData.height,
    });

    if (result) {
      if (result === "close") return { applyNodes: applyNodes, isMove: isMove };
      stopGridEditing();

      const resultArr = result[0];
      const transformedData = {
        code: resultArr[columns[0]] || "",
        name: resultArr[columns[1]] || "",
        spec: resultArr[columns[2]] || "",
        unit: resultArr[columns[3]] || "",
        material: resultArr[columns[4]] || "",
        ...resultArr, // 나머지 값을 그대로 추가
      };

      const columnMappings: Record<
        string,
        { target: string; source: keyof typeof transformedData }[]
      > = {
        cd_wh_ref: [
          { target: "cd_wh_ref", source: "code" },
          { target: "nm_wh_ref", source: "name" },
        ],
        cd_wh_isu: [
          { target: "cd_wh_isu", source: "code" },
          { target: "nm_wh_isu", source: "name" },
        ],
        cd_wh_rcv: [
          { target: "cd_wh_rcv", source: "code" },
          { target: "nm_wh_rcv", source: "name" },
        ],
        cd_wh_good: [
          { target: "cd_wh_good", source: "code" },
          { target: "nm_wh_good", source: "name" },
        ],
        cd_wh_bad: [
          { target: "cd_wh_bad", source: "code" },
          { target: "nm_wh_bad", source: "name" },
        ],
        cd_cust_prd: [
          { target: "cd_cust_prd", source: "code" },
          { target: "nm_cust_prd", source: "name" },
        ],
        cd_matl: [
          { target: "cd_matl", source: "code" },
          { target: "nm_matl", source: "name" },
          { target: "dc_item_spec_matl", source: "dc_item_spec" },
          { target: "dc_item_unit_matl", source: "dc_item_unit" },
          { target: "dc_material_matl", source: "dc_material" },
        ],
        cd_cust_dlv: [
          { target: "cd_cust_dlv", source: "code" },
          { target: "nm_cust_dlv", source: "name" },
        ],
      };

      if (columnMappings[colId]) {
        columnMappings[colId].forEach(({ target, source }, idx) => {
          if (skipRest(rowNode, idx, target)) return;
          if (columnFields.has(target)) {
            setPopupValue(rowNode, target, transformedData[source]);
          }
        });
      } else {
        if (colId == columns[0]) {
          columns.forEach((column: string, idx: number) => {
            if (skipRest(rowNode, idx, column)) return;
            if (columnFields.has(column)) {
              setPopupValue(rowNode, column, resultArr[column]);
            } else if (rowNode.data && column in rowNode.data) {
              // 26.08.21 ysw 그리드 컬럼설정에 없는 필드(표시용 nm_xxx, cd_dept 등)는
              // setDataValue가 무동작이라 이전 값이 그대로 남는다. 데이터에 직접 반영한다.
              rowNode.data[column] = resultArr[column];
            }
          });
        } else {
          setPopupValue(rowNode, colId, resultArr[columns[0]]);
          setPopupValue(
            rowNode,
            colId.replace("cd_", "nm_"),
            resultArr[columns[1]]
          );
        }
      }

      // 공통말고 추가로 팝업에서 받고싶은 데이터 mapping
      popupApply &&
        popupApply.map((data: any) => {
          const target = data.target;
          const source = data.source;

          setPopupValue(rowNode, target, transformedData[source]);
        });
      isMove = true;
    }
  } else if (colType === "SnMultiCodeBox") {
    applyNodes.push(rowNode);
    const popupData = getSearchData(popupId, "");

    const { columns } = popupSetting;

    const rtn: any = await getInitPopUpData(popupId, {});
    const mergedData = {
      ...rtn.formData,
      ...keyValueObject,
      cd_clas: cdClas || "",
    };
    const multiPopup = (
      <MultiPopup
        code={popupId}
        selectedData={mergedData}
        autoSearch={popupData.autoSearch}
      />
    );

    const results = await showPopupModal({
      title: popupData.title,
      children: multiPopup,
      width: popupData.width,
      height: popupData.height,
    });

    if (results) {
      if (results === "close")
        return { applyNodes: applyNodes, isMove: isMove };
      stopGridEditing();
      {
        results.map((result: any, index: number) => {
          const resultArr = result;
          const transformedData = {
            code: resultArr[columns[0]] || "",
            name: resultArr[columns[1]] || "",
            spec: resultArr[columns[2]] || "",
            unit: resultArr[columns[3]] || "",
            material: resultArr[columns[4]] || "",
            ...resultArr, // 나머지 값을 그대로 추가
          };

          const columnMappings: Record<
            string,
            { target: string; source: keyof typeof transformedData }[]
          > = {
            cd_wh_ref: [
              { target: "cd_wh_ref", source: "code" },
              { target: "nm_wh_ref", source: "name" },
            ],
            cd_wh_isu: [
              { target: "cd_wh_isu", source: "code" },
              { target: "nm_wh_isu", source: "name" },
            ],
            cd_wh_rcv: [
              { target: "cd_wh_rcv", source: "code" },
              { target: "nm_wh_rcv", source: "name" },
            ],
            cd_wh_good: [
              { target: "cd_wh_good", source: "code" },
              { target: "nm_wh_good", source: "name" },
            ],
            cd_wh_bad: [
              { target: "cd_wh_bad", source: "code" },
              { target: "nm_wh_bad", source: "name" },
            ],
            cd_cust_prd: [
              { target: "cd_cust_prd", source: "code" },
              { target: "nm_cust_prd", source: "name" },
            ],
            cd_matl: [
              { target: "cd_matl", source: "code" },
              { target: "nm_matl", source: "name" },
              { target: "dc_item_spec_matl", source: "dc_item_spec" },
              { target: "dc_item_unit_matl", source: "dc_item_unit" },
              { target: "dc_material_matl", source: "dc_material" },
            ],
            cd_cust_dlv: [
              { target: "cd_cust_dlv", source: "code" },
              { target: "nm_cust_dlv", source: "name" },
            ],
          };
          if (index === 0) {
            if (columnMappings[colId]) {
              columnMappings[colId].forEach(({ target, source }, idx) => {
                if (skipRest(rowNode, idx, target)) return;
                if (columnFields.has(target)) {
                  setPopupValue(rowNode, target, transformedData[source]);
                }
              });
            } else {
              if (colId == columns[0]) {
                columns.forEach((column: string, idx: number) => {
                  if (skipRest(rowNode, idx, column)) return;
                  if (columnFields.has(column)) {
                    setPopupValue(rowNode, column, resultArr[column]);
                  } else if (rowNode.data && column in rowNode.data) {
                    // 26.08.21 ysw 그리드 컬럼설정에 없는 필드(표시용 nm_xxx, cd_dept 등)는
                    // setDataValue가 무동작이라 이전 값이 그대로 남는다. 데이터에 직접 반영한다.
                    rowNode.data[column] = resultArr[column];
                  }
                });
              } else {
                setPopupValue(rowNode, colId, resultArr[columns[0]]);
                setPopupValue(
                  rowNode,
                  colId.replace("cd_", "nm_"),
                  resultArr[columns[1]]
                );
              }

              // 공통말고 추가로 팝업에서 받고싶은 데이터 mapping
              popupApply &&
                popupApply.map((data: any) => {
                  const target = data.target;
                  const source = data.source;
                  setPopupValue(rowNode, target, transformedData[source]);
                });
            }
          } else {
            const addNode: RowNode | null = addRow();
            applyNodes.push(addNode!);
            if (addNode) {
              if (columnMappings[colId]) {
                columnMappings[colId].forEach(({ target, source }, idx) => {
                  if (skipRest(addNode, idx, target)) return;
                  if (columnFields.has(target)) {
                    setPopupValue(addNode, target, transformedData[source]);
                  }
                });
              } else {
                if (colId == columns[0]) {
                  columns.forEach((column: string, idx: number) => {
                    if (skipRest(addNode, idx, column)) return;
                    if (columnFields.has(column)) {
                      setPopupValue(addNode, column, resultArr[column]);
                    } else if (addNode.data && column in addNode.data) {
                      // 26.08.21 ysw 그리드 컬럼설정에 없는 필드(표시용 nm_xxx, cd_dept 등)는
                      // setDataValue가 무동작이라 이전 값이 그대로 남는다. 데이터에 직접 반영한다.
                      addNode.data[column] = resultArr[column];
                    }
                  });
                } else {
                  setPopupValue(addNode, colId, resultArr[columns[0]]);
                  setPopupValue(
                    addNode,
                    colId.replace("cd_", "nm_"),
                    resultArr[columns[1]]
                  );
                }

                // 공통말고 추가로 팝업에서 받고싶은 데이터 mapping
                popupApply &&
                  popupApply.map((data: any) => {
                    const target = data.target;
                    const source = data.source;
                    setPopupValue(addNode, target, transformedData[source]);
                  });
              }
            }
          }
        });
      }
      isMove = true;
    }
  }

  // 26.08.21 ysw SnCodeBox 셀은 cd_displaycolumn(다른 필드)을 그리기 때문에
  // 코드 컬럼만 바뀐 것으로 본 ag-Grid가 셀을 다시 그리지 않아 표시값이 이전 값으로 남는다.
  // (예: cd_emp는 바뀌었는데 화면에는 이전 nm_emp가 계속 보임)
  // 팝업으로 값이 반영된 행은 강제로 다시 그린다.
  if (applyNodes.length > 0) {
    const api = gridApi?.current ?? gridApi;
    api?.refreshCells?.({
      rowNodes: applyNodes.filter(Boolean),
      force: true,
    });
  }

  return { applyNodes: applyNodes, isMove: isMove };
}

// 팝업 관련 데이터 삭제 함수
export function deleteKeyDown(
  event: any,
  selectedSingleSetting: any[],
  selectedMultiSetting: any[],
  popupSetting: { single: any[]; multi: any[]; all: any[] },
  colId: string,
  memoizedColumnConfig: ColumnConfig[] | undefined
) {
  const Setting = PopupSettings[popupSetting.single[0]?.cd_popup];
  const { columns } = Setting;
  const rowNode = event.node; // 현재 행 가져오기
  if (rowNode && rowNode.data) {
    if (memoizedColumnConfig) {
      const columnFields = new Set(
        memoizedColumnConfig.map((col) => col.cd_column)
      );
      const clearColumnMappings: Record<string, string[]> = {
        cd_wh_ref: ["cd_wh_ref", "nm_wh_ref"],
        cd_wh_isu: ["cd_wh_isu", "nm_wh_isu"],
        cd_wh_rcv: ["cd_wh_rcv", "nm_wh_rcv"],
        cd_wh_good: ["cd_wh_good", "nm_wh_good"],
        cd_wh_bad: ["cd_wh_bad", "nm_wh_bad"],
        cd_cust_prd: ["cd_cust_prd", "nm_cust_prd"],
        cd_matl: [
          "cd_matl",
          "nm_matl",
          "dc_item_spec_matl",
          "dc_item_unit_matl",
          "dc_material_matl",
        ],
      };

      // 26.08.21 kdy PopupHelper의 clearField와 동일한 이유. 컬럼이 없으면 행 데이터를 직접 비운다.
      const clearField = (field: string) => {
        if (!field) return;
        if (columnFields.has(field)) {
          rowNode.setDataValue(field, "");
          return;
        }
        if (field in rowNode.data) {
          rowNode.data[field] = "";
        }
      };

      if (clearColumnMappings[colId]) {
        clearColumnMappings[colId].forEach((field) => {
          clearField(field);
        });
      } else {
        columns.forEach((column: string) => {
          clearField(column);
        });
      }

      // 26.08.21 kdy SnCodeBox는 cd_displaycolumn(이름 필드)을 코드 셀에 그리므로
      // 컬럼 없는 이름을 데이터에서만 비우면 셀이 다시 안 그려져 옛 이름이 남는다.
      event.api?.refreshCells?.({ rowNodes: [rowNode], force: true });
    }
  }
}

const checkLastColumn = async (api: any, colId: string) => {
  const displayedCols = api.current.getAllDisplayedColumns();
  const isLastDisplayed =
    displayedCols[displayedCols.length - 1].getColId() === colId;
  return isLastDisplayed;
};
